import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { message, Spin, Tooltip } from 'antd';
import { SearchOutlined, PlayCircleOutlined, AppstoreOutlined } from '@ant-design/icons';
import { WorkflowMarket } from '../types';
import { workflowMarketApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PageContainer from '../components/PageContainer';
import RetryImage from '../components/RetryImage';

// 获取封面图片完整URL（拼接资源服务器域名）
const getFullImageUrl = (coverImageUrl: string | undefined): string | undefined => {
  if (!coverImageUrl) return undefined;
  // 如果已经是完整URL（http/https开头），直接返回
  if (coverImageUrl.startsWith('http://') || coverImageUrl.startsWith('https://')) {
    return coverImageUrl;
  }
  // 拼接资源服务器域名
  const resourceBaseUrl = import.meta.env.VITE_RESOURCE_BASE_URL || '';
  return `${resourceBaseUrl}${coverImageUrl}`;
};

// Split workflow market labels configured with comma or pipe separators.
const parseMarketLabels = (label?: string): string[] => {
  return (label || '')
    .split(/[,\uFF0C|｜]+/)
    .map(item => item.trim())
    .filter(Boolean);
};

const WorkflowMarketPageNexus: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [projects, setProjects] = useState<WorkflowMarket[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<WorkflowMarket[]>([]);
  const [loading, setLoading] = useState(true);
  
  // 从 URL 参数初始化状态
  const [searchText, setSearchText] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') || 'all');
  const [categories, setCategories] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('pageSize') || '12'));
  
  // 保存搜索前的页码，用于清空搜索时恢复
  const pageBeforeSearch = useRef<number | null>(null);

  // 加载项目数据
  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await workflowMarketApi.getProjects(user?.user_id, user?.role_id);
      
      if (response.success && response.data) {
        setProjects(response.data);
        setFilteredProjects(response.data);
        
        const uniqueCategories = Array.from(
          response.data
            .filter(p => p.category)
            .reduce((categoryMap, project) => {
              const category = project.category as string;
              const displayOrder = project.category_display_order ?? Number.MAX_SAFE_INTEGER;
              const currentOrder = categoryMap.get(category);

              if (currentOrder === undefined || displayOrder < currentOrder) {
                categoryMap.set(category, displayOrder);
              }

              return categoryMap;
            }, new Map<string, number>())
            .entries()
        )
          .sort((a, b) => a[1] - b[1])
          .map(([category]) => category);
        setCategories(uniqueCategories);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      message.error('加载工作流失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理搜索文本变化
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const previousSearchText = searchText;
    
    console.log('🔍 搜索变化:', {
      previousSearchText,
      newValue: value,
      currentPage,
      pageBeforeSearch: pageBeforeSearch.current
    });
    
    setSearchText(value);
    
    // 从无搜索变为有搜索：保存当前页码，然后重置到第1页
    if (!previousSearchText && value) {
      console.log('✅ 开始搜索，保存页码:', currentPage);
      pageBeforeSearch.current = currentPage;
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
    // 从有搜索变为无搜索：恢复到搜索前的页码
    else if (previousSearchText && !value) {
      console.log('🔄 清空搜索，恢复页码:', pageBeforeSearch.current);
      if (pageBeforeSearch.current !== null && pageBeforeSearch.current !== 1) {
        setCurrentPage(pageBeforeSearch.current);
      }
      pageBeforeSearch.current = null;
    }
    // 已在搜索中修改搜索内容：保持在第1页
    else if (previousSearchText && value && currentPage !== 1) {
      console.log('📝 修改搜索内容，重置到第1页');
      setCurrentPage(1);
    }
  };

  // 处理分类选择变化
  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    // 切换分类时重置到第一页
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // 更新 URL 参数
  useEffect(() => {
    const params: any = {};
    if (currentPage > 1) params.page = currentPage.toString();
    if (pageSize !== 12) params.pageSize = pageSize.toString();
    if (searchText) params.search = searchText;
    if (selectedCategory !== 'all') params.category = selectedCategory;
    
    setSearchParams(params, { replace: true });
  }, [currentPage, pageSize, searchText, selectedCategory]);

  // 过滤项目
  useEffect(() => {
    let filtered = projects;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(project => project.category === selectedCategory);
    }

    if (searchText) {
      const normalizedSearchText = searchText.toLowerCase();
      filtered = filtered.filter(project =>
        (project.display_name || '').toLowerCase().includes(normalizedSearchText) ||
        (project.description || '').toLowerCase().includes(normalizedSearchText) ||
        parseMarketLabels(project.label).some(label => label.toLowerCase().includes(normalizedSearchText))
      );
    }

    setFilteredProjects(filtered);
  }, [searchText, selectedCategory, projects]);

  useEffect(() => {
    if (user) {
      loadProjects();
    }
  }, [user?.user_id]);

  const handleExecute = (project: WorkflowMarket) => {
    const marketId = project.market_id || project.id;
    if (marketId) {
      // 保存当前页面状态到 URL，以便返回时恢复
      const currentState = {
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
        search: searchText,
        category: selectedCategory
      };
      // 将状态保存到 sessionStorage，作为备用方案
      sessionStorage.setItem('workflowMarketState', JSON.stringify(currentState));
      
      navigate(`/ai-conversation/${marketId}`);
    } else {
      message.warning('工作流配置错误');
    }
  };

  // 分页计算
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedProjects = filteredProjects.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredProjects.length / pageSize);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Banner */}
        <div className="mb-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-center">
          <div className="flex items-center justify-center mb-3">
            <AppstoreOutlined style={{ fontSize: '32px', color: 'white', marginRight: '12px' }} />
            <h1 className="text-4xl font-bold" style={{ color: 'white' }}>{user?.workflow_main_title || '工作流市场'}</h1>
          </div>
          <p className="text-lg" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{user?.workflow_sub_title || '探索和使用各种AI工作流，让创意无限可能'}</p>
        </div>
      {/* 搜索和筛选区域 */}
      <div className="mb-8 flex flex-col md:flex-row gap-4">
        {/* 搜索框 */}
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="搜索项目名称、描述或标签"
            value={searchText}
            onChange={handleSearchChange}
            className="w-full px-4 py-3 pl-12 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
          <SearchOutlined 
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
            style={{ fontSize: '20px' }}
          />
        </div>

        {/* 分类筛选 */}
        <div className="relative">
          <select
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="px-6 py-3 pr-10 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all cursor-pointer appearance-none min-w-[150px]"
          >
            <option value="all">全部分类</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* 结果计数 */}
        <div className="flex items-center px-4 py-3 bg-indigo-50 text-indigo-700 rounded-xl font-medium">
          共找到 {filteredProjects.length} 个项目
        </div>
      </div>

      {/* 项目网格 */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Spin size="large" />
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="text-center py-20">
          <AppstoreOutlined style={{ fontSize: '64px', color: '#9CA3AF' }} />
          <p className="mt-4 text-gray-500 text-lg">暂无工作流</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {paginatedProjects.map((project) => {
            const labels = parseMarketLabels(project.label);
            return (
            <div
              key={project.market_id || project.id}
              className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-indigo-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
            >
              {/* 项目图片 - 固定宽高比 */}
              {project.cover_image_url ? (
                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-indigo-50 to-purple-50">
                  <RetryImage
                    src={getFullImageUrl(project.cover_image_url)}
                    alt={project.display_name || project.project_name}
                    className="group-hover:scale-110 transition-transform duration-300"
                    maxRetries={4}
                    retryDelay={2000}
                  />
                </div>
              ) : (
                <div className="aspect-[4/3] bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                  <AppstoreOutlined style={{ fontSize: '48px', color: '#6366F1' }} />
                </div>
              )}

              {/* 项目信息 */}
              <div className="p-6">
                <Tooltip title={project.display_name || project.project_name}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1 group-hover:text-indigo-600 transition-colors">
                    {project.display_name || project.project_name}
                  </h3>
                </Tooltip>
                <Tooltip title={project.description || project.project_description || '暂无描述'}>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2 min-h-[40px]">
                    {project.description || project.project_description || '暂无描述'}
                  </p>
                </Tooltip>

                {/* 标签和日期 */}
                <div className="flex items-center justify-between mb-4 gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {project.category && (
                      <span className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full font-medium truncate">
                        {project.category}
                      </span>
                    )}
                    {project.access_type === 'pro' && (
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full font-semibold border border-amber-200">
                        PRO
                      </span>
                    )}
                  </div>
                  {labels.length > 0 && (
                    <div className="flex items-center justify-end gap-1 flex-wrap shrink-0 max-w-[55%]">
                      {labels.map(label => (
                        <span key={label} className="px-2 py-1 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-xs rounded-full font-semibold shadow-sm border border-white/50">
                          {label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* 执行按钮 */}
                <button
                  onClick={() => handleExecute(project)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-indigo-500/50 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <PlayCircleOutlined className="text-white" style={{ fontSize: '18px' }} />
                  <span>执行</span>
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* 分页 */}
      {!loading && filteredProjects.length > 0 && (
          <div className="mt-8 flex flex-col md:flex-row justify-between items-center gap-4 bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
            <div className="text-sm text-gray-600 font-medium">
              显示第 <span className="text-indigo-600 font-semibold">{startIndex + 1}</span> - <span className="text-indigo-600 font-semibold">{Math.min(endIndex, filteredProjects.length)}</span> 条，
              共 <span className="text-indigo-600 font-semibold">{filteredProjects.length}</span> 条
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-5 py-2.5 bg-white border-2 border-gray-200 rounded-xl hover:border-indigo-400 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:bg-white transition-all font-medium text-gray-700"
              >
                上一页
              </button>
              <div className="flex items-center space-x-2">
                <span className="px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold min-w-[80px] text-center">
                  {currentPage} / {totalPages}
                </span>
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-5 py-2.5 bg-white border-2 border-gray-200 rounded-xl hover:border-indigo-400 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:bg-white transition-all font-medium text-gray-700"
              >
                下一页
              </button>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-4 py-2.5 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 cursor-pointer hover:border-indigo-400 transition-all font-medium text-gray-700"
              >
                <option value={12}>12条/页</option>
                <option value={24}>24条/页</option>
                <option value={48}>48条/页</option>
              </select>
            </div>
          </div>
      )}
      </div>
    </div>
  );
};

export default WorkflowMarketPageNexus;
